'use client'

import { ReactNode, useState } from 'react'
import { IconBuilding, IconHome2 } from '@tabler/icons-react'
import { usePathname, useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'

import { getQueryClient } from 'shared/api'
import Button from 'shared/ui/Button'
import { Modal, ModalTitle } from 'shared/ui/Modal'
import { KEYBOARD_KEYS } from 'shared/ui/types'
import toast from 'shared/ui/utils/toast'

import { useInvoiceAddressPort } from '../../../../ports'
import { extractTokenFromClientSession } from '../../../../session'
import { deleteInvoiceAddress } from '../../api'
import { CustomerInvoiceAddress, ResponseStatus } from '../../types'

import styles from './ModalRemoveInvoiceAddress.module.scss'

type ModalRemoveInvoiceAddressTypes = {
  trigger: ReactNode
  address: CustomerInvoiceAddress
}

const ModalRemoveInvoiceAddress = ({
  trigger,
  address
}: ModalRemoveInvoiceAddressTypes) => {
  const [open, setOpen] = useState(false)
  const [submitState, setSubmitState] = useState<ResponseStatus>(
    ResponseStatus.initial
  )

  const { onInvoiceAddressRemoved } = useInvoiceAddressPort()

  const router = useRouter()
  const pathname = usePathname()
  const session = useSession()
  const token = extractTokenFromClientSession(session)
  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const handleConfirmClick = async () => {
    if (submitState !== ResponseStatus.pending) {
      await onSubmit()
    }
  }

  const _handleClose = async () => {
    /**
     * Refresh props from getServerSideProps
     * More: https://www.joshwcomeau.com/nextjs/refreshing-server-side-props/
     */
    void router.replace(pathname || '')
    handleClose()
  }

  const onSubmit = async () => {
    if (!address) {
      setSubmitState(ResponseStatus.rejected)
    }

    setSubmitState(ResponseStatus.pending)

    try {
      await deleteInvoiceAddress(address.id, token)

      await _handleClose()

      toast.success(`Adres "${address.name}" został usunięty.`)
      setSubmitState(ResponseStatus.fulfilled)

      // Wait for the addresses to be refetched before updating invoice data
      await getQueryClient().refetchQueries({ queryKey: ['invoiceAddresses'] })

      await onInvoiceAddressRemoved(address.id)
    } catch (error) {
      void getQueryClient().refetchQueries({ queryKey: ['invoiceAddresses'] })
      setSubmitState(ResponseStatus.rejected)
    }
  }

  return (
    <>
      <div
        className={styles.triggerWrapper}
        onClick={handleOpen}
        tabIndex={0}
        role='button'
        aria-label='Usuń adres'
        onKeyDown={e => {
          if (e.key === KEYBOARD_KEYS.ENTER) {
            e.preventDefault()
            e.stopPropagation()
            void handleOpen()
          }
        }}
      >
        {trigger}
      </div>
      <Modal
        id='remove-invoice-address-modal'
        open={open}
        onClose={handleClose}
        size='small'
      >
        <ModalTitle>Potwierdź usunięcie adresu</ModalTitle>
        <div className={styles.summaryWrapper}>
          <div
            className={styles.icon}
            color={address.iconColor}
            style={{ '--icon-color': address.iconColor } as React.CSSProperties}
          >
            {address.iconType === 'home' ? <IconHome2 /> : <IconBuilding />}
          </div>
          <div>
            <div className={styles.addressName}>{address.name}</div>
            <p>
              {address?.street} {address?.houseNumber}{' '}
              {address?.flatNumber ? `m. ${address.flatNumber}` : null}
            </p>
          </div>
        </div>
        <div className={styles.buttonsWrapper}>
          <Button
            fullWidth
            type='button'
            disabled={submitState === ResponseStatus.pending}
            onClick={handleConfirmClick}
          >
            Potwierdź
          </Button>
          <Button fullWidth variant='neutralOutline' onClick={handleClose}>
            Anuluj
          </Button>
        </div>
      </Modal>
    </>
  )
}

export default ModalRemoveInvoiceAddress
